<template>
  <div>
    <Feature :feature-slug="featureNames.GOOGLE_REVIEW">
      <v-divider class="mb-6" />
      <div class="mb-6">
        <GoogleReviewLinkInput
          :company-id="wizardCompany?.id"
          :model-value="formData.googleReviewLink"
          :show-success-snackbar="false"
          :text="{
            label: $t('onboarding.googleReview.label'),
            description: $t('onboarding.googleReview.description'),
            learnLinkLabel: $t('onboarding.googleReview.learnLinkLabel'),
            helpUrl: $t('onboarding.googleReview.helpUrl'),
            placeholder: $t('onboarding.googleReview.placeholder'),
            error: {
              general: $t('onboarding.googleReview.error.general'),
              invalid: $t('onboarding.googleReview.error.invalid'),
            },
          }"
          @update:model-value="updateField('googleReviewLink', $event || '')"
        />
      </div>
    </Feature>

    <Feature :feature-slug="featureNames.MAKLER_MITTEL">
      <v-divider class="my-6" />
      <ChatWidgetIntegration
        form-key="automaticIntegration"
        :initial-value="widgetInitialValue"
        @confirm="onWidgetIntegrationConfirm"
      />
    </Feature>

    <v-divider class="my-6" />
    <h3 class="text-subtitle-1 font-weight-bold mb-2">
      {{ $t('onboarding.wizard.review.legal-urls-title') }}
    </h3>
    <p class="text-body-2 text-medium-emphasis mb-4">
      {{ $t('onboarding.wizard.review.legal-urls-hint') }}
    </p>
    <v-text-field
      class="mb-3"
      :model-value="formData.linkImprint"
      variant="outlined"
      density="comfortable"
      rounded="lg"
      :label="$t('onboarding.company-data.web.imprint')"
      :placeholder="$t('onboarding.company-data.web.imprintPlaceholder')"
      hide-details
      @update:model-value="updateField('linkImprint', $event)"
    />
    <v-text-field
      class="mb-3"
      :model-value="formData.linkPrivacy"
      variant="outlined"
      density="comfortable"
      rounded="lg"
      :label="$t('onboarding.company-data.web.privacy')"
      :placeholder="$t('onboarding.company-data.web.privacyPlaceholder')"
      hide-details
      @update:model-value="updateField('linkPrivacy', $event)"
    />
    <v-text-field
      :model-value="formData.linkAbout"
      variant="outlined"
      density="comfortable"
      rounded="lg"
      :label="$t('onboarding.company-data.web.about')"
      :placeholder="$t('onboarding.company-data.web.aboutPlaceholder')"
      hide-details
      @update:model-value="updateField('linkAbout', $event)"
    />

    <v-divider class="my-6" />
    <OnboardingLegalConsents
      v-model="legalConsentsModel"
      :company="wizardCompany"
    />
  </div>
</template>

<script setup lang="ts">
import { computed } from 'vue'

import ChatWidgetIntegration from '@/components/ai/onboarding/ChatWidgetIntegration.vue'
import OnboardingLegalConsents, {
  type LegalConsentsValues,
} from '@/components/ai/onboarding/OnboardingLegalConsents.vue'
import GoogleReviewLinkInput from '@/components/GoogleReviewLinkInput.vue'
import featureNames from '@/lib/featureNames'
import { Feature } from '@/plugins/feature-config'

import { useOnboardingDataEntryContext } from '../../composables/onboardingDataEntryContext'
import type { OnboardingWizardCompany } from '../../lib/onboardingWizardCompany'

const props = defineProps<{
  wizardCompany?: OnboardingWizardCompany | null
  legalConsents?: LegalConsentsValues | null
}>()

const emit = defineEmits<{
  'update:legalConsents': [value: LegalConsentsValues]
  'widget-integration': [payload: { enabled: boolean }]
}>()

const { formData, aiCapturedFields, updateField } = useOnboardingDataEntryContext()

const EMPTY_LEGAL_CONSENTS: LegalConsentsValues = {
  acceptAgb: false,
  acceptPrivacy: false,
  acceptGuideline: false,
  contactViaEmail: false,
  contactViaWhatsapp: false,
  acceptConfirmData: false,
}

const legalConsentsModel = computed<LegalConsentsValues>({
  get: () => props.legalConsents ?? EMPTY_LEGAL_CONSENTS,
  set: (value) => emit('update:legalConsents', value),
})

const widgetInitialValue = computed(() =>
  formData.value.automaticIntegration === true
    ? true
    : formData.value.automaticIntegration === false &&
        aiCapturedFields.value.has('automaticIntegration')
      ? false
      : null
)

const onWidgetIntegrationConfirm = ({ enabled }: { enabled: boolean }) => {
  updateField('automaticIntegration', enabled)
  emit('widget-integration', { enabled })
}
</script>
